import React, { useState } from 'react';
import '../styles/OpenScoreCard.css';
import { ChowBadge } from '../assets/images';
import api from '../services/api';
import { CareHome } from '../types';
import {
    formatCost,
    formatFreshness,
    formatInspectionTime,
    formatScore,
    getSingleDayEvents,
    getScoreColors
} from '../utils/careHomeCardFormatting';
import EventPopup from './EventPopup';

interface OpenScoreCardProps {
    careHome: CareHome;
    onSelect?: (careHome: CareHome) => void;
    userId?: string;
    startingPostCode?: string;
    searchPosition?: number;
    eventFilterActive?: boolean;
}

const OpenScoreCard: React.FC<OpenScoreCardProps> = ({
    careHome,
    onSelect,
    userId = '',
    startingPostCode = '',
    searchPosition = 0,
    eventFilterActive = false
}) => {
    const score = formatScore(careHome.open_score);
    const scoreColors = getScoreColors(careHome.open_score);
    const freshnessText = formatFreshness(careHome.freshness_days);
    const feeDisplay = formatCost(careHome.relative_fees);
    const inspectionTime = formatInspectionTime(careHome.Days_Since_Last_Inspected_CQC);
    const singleDayEvents = getSingleDayEvents(careHome.events);
    const scoreSize = 48;
    const scoreRightOffset = 14;
    const interactive = Boolean(onSelect);
    const [showEventPopup, setShowEventPopup] = useState(false);

    const trackAction = (action: 'details' | 'contact') => {
        api.trackUserAction({
            regulator_id: careHome.reg_id,
            user_action: action,
            user_id: userId,
            starting_post_code: startingPostCode,
            search_position: searchPosition
        });
    };

    const handleSelect = () => {
        onSelect?.(careHome);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleSelect();
        }
    };

    const handleActionClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
    };

    const cardStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        maxWidth: '352px',
        margin: '0 auto',
        borderRadius: '16px',
        background: 'transparent',
        color: '#4f5960',
        fontFamily: '"Trebuchet MS", sans-serif',
        overflow: 'visible',
        boxShadow: '0 10px 26px rgba(0, 0, 0, 0.12)'
    };

    const headerStyle: React.CSSProperties = {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        minHeight: '50px',
        padding: `0.58rem ${scoreSize + 20}px 0.58rem 0.78rem`,
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
        background: 'linear-gradient(135deg, #025150 0%, #0a7f72 100%)',
        color: '#ffffff',
        overflow: 'visible'
    };

    const titleStyle: React.CSSProperties = {
        margin: 0,
        flex: '1 1 auto',
        minWidth: 0,
        color: 'inherit',
        fontSize: '0.98rem',
        fontWeight: 700,
        lineHeight: 1.08,
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };

    const scoreStyle: React.CSSProperties = {
        position: 'absolute',
        top: '100%',
        right: `${scoreRightOffset}px`,
        transform: 'translateY(-50%)',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${scoreSize}px`,
        height: `${scoreSize}px`,
        borderRadius: '999px',
        border: '2px solid #d9f4dc',
        background: `linear-gradient(160deg, ${scoreColors.color1} 0%, ${scoreColors.color2} 100%)`,
        boxShadow: '0 3px 8px rgba(12, 95, 87, 0.12)',
        color: '#ffffff',
        fontSize: '1.02rem',
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: '-0.02em',
        textAlign: 'center',
        whiteSpace: 'nowrap'
    };

    const bodyStyle: React.CSSProperties = {
        padding: '0.62rem 0.84rem 0.62rem',
        border: '1px solid rgba(2, 81, 80, 0.12)',
        borderTop: 0,
        borderBottomLeftRadius: '16px',
        borderBottomRightRadius: '16px',
        background: '#f4f3f1'
    };

    const validatedStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.36rem',
        marginBottom: '0.42rem',
        paddingRight: `${scoreSize + scoreRightOffset - 4}px`,
        color: '#0d5f57',
        fontSize: '0.74rem',
        lineHeight: 1.05,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };

    const infoStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '0.7rem'
    };

    const metricStyle: React.CSSProperties = {
        flex: '1 1 auto',
        minWidth: 0
    };

    const feeMetricStyle: React.CSSProperties = {
        flex: '0 0 88px',
        minWidth: '88px'
    };

    const labelStyle: React.CSSProperties = {
        margin: '0 0 0.14rem',
        color: '#727982',
        fontSize: '0.68rem',
        lineHeight: 1.08,
        whiteSpace: 'nowrap'
    };

    const valueStyle: React.CSSProperties = {
        margin: 0,
        color: '#0d5f57',
        fontSize: '0.86rem',
        lineHeight: 1.02,
        fontWeight: 700
    };

    const dividerStyle: React.CSSProperties = {
        height: '2px',
        margin: '0.38rem 0 0.34rem',
        background: '#1ba395'
    };

    const footerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '0.38rem',
        flexWrap: 'nowrap'
    };

    const pillStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '30px',
        padding: '0.3rem 0.68rem',
        borderRadius: '16px',
        border: '1.5px solid #d5d8dc',
        background: '#f8f8f8',
        color: '#4f5960',
        textDecoration: 'none',
        fontSize: '0.76rem',
        lineHeight: 1
    };

    return (
        <article
            className={[
                'open-score-card',
                interactive ? 'open-score-card--interactive' : ''
            ].filter(Boolean).join(' ')}
            style={cardStyle}
            onClick={interactive ? handleSelect : undefined}
            onKeyDown={interactive ? handleKeyDown : undefined}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
        >
            <header className="open-score-card__header" style={headerStyle}>
                <h3 className="open-score-card__title" style={titleStyle}>{careHome.organisation_name}</h3>
                {eventFilterActive ? (
                    <div className="open-score-card__score" style={{
                        ...scoreStyle,
                        background: 'none',
                        border: 'none',
                        boxShadow: 'none',
                        padding: 0
                    }}>
                        <img src={ChowBadge} alt="Care Home Open Week" width={scoreSize} height={scoreSize} style={{ borderRadius: '999px', display: 'block' }} />
                    </div>
                ) : (
                    <div className="open-score-card__score" aria-label={`OpenScore ${score}`} style={scoreStyle}>
                        {score}
                    </div>
                )}
            </header>

            <div className="open-score-card__body" style={bodyStyle}>
                <div className="open-score-card__validated" style={validatedStyle}>
                    <span>Data last validated: {freshnessText}</span>
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: '14px', height: '14px', flex: '0 0 auto', display: 'block' }}>
                        <circle cx="12" cy="12" r="10" fill="#0a7f72" />
                        <path d="M8 12.2l2.4 2.5L16.5 9" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>

                <div className="open-score-card__info" style={infoStyle}>
                    <div className="open-score-card__metric" style={metricStyle}>
                        <p className="open-score-card__label" style={labelStyle}>Regulator Last Inspection:</p>
                        <p className="open-score-card__value" style={valueStyle}>{inspectionTime}</p>
                    </div>

                    {/* Hide fees/pound signs when an event filter is active (update 2026_04_22.3) */}
                    {!eventFilterActive && (
                        <div className="open-score-card__metric open-score-card__metric--fee" style={feeMetricStyle}>
                            <p className="open-score-card__label" style={labelStyle}>Fee</p>
                            <p className="open-score-card__value open-score-card__value--fee" style={{ ...valueStyle, letterSpacing: '0.04em' }}>
                                <span>{feeDisplay.active}</span>
                                {feeDisplay.muted && (
                                    <span className="open-score-card__fee-muted" style={{ color: '#c8ccd1' }}>{feeDisplay.muted}</span>
                                )}
                            </p>
                        </div>
                    )}
                </div>

                <div className="open-score-card__divider" style={dividerStyle} />

                <div className="open-score-card__footer" style={footerStyle}>
                    {singleDayEvents.length > 0 && (
                        <button
                            className="open-score-card__event-btn"
                            style={{
                                display: 'inline-flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                minHeight: '30px',
                                padding: '0.24rem 0.62rem',
                                borderRadius: '16px',
                                border: '1.5px solid #7b43a8',
                                background: 'linear-gradient(135deg, #7a43a4 0%, #8a56b0 100%)',
                                boxShadow: '0 5px 12px rgba(106, 27, 154, 0.16)',
                                color: '#ffffff',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                lineHeight: 1.15,
                                cursor: 'pointer',
                                marginRight: 'auto',
                                whiteSpace: 'nowrap',
                            }}
                            onClick={(event) => {
                                handleActionClick(event);
                                setShowEventPopup(true);
                            }}
                        >
                            <span>Event: {singleDayEvents[0].formattedDate}</span>
                            {singleDayEvents.length > 1 && (
                                <span style={{ fontSize: '0.6rem', fontWeight: 500, color: '#ead8f6', marginTop: '1px' }}>
                                    + {singleDayEvents.length - 1} more&hellip;
                                </span>
                            )}
                        </button>
                    )}
                    <a
                        href={`https://www.autumna.co.uk/providers/${careHome.slug}/?utm_source=OpenScoreMap&utm_medium=details`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="open-score-card__action"
                        style={pillStyle}
                        onClick={(event) => {
                            handleActionClick(event);
                            trackAction('details');
                        }}
                    >
                        Details
                    </a>

                    <a
                        href={`https://www.autumna.co.uk/providers/contact/${careHome.id}/?utm_source=OpenScoreMap&utm_medium=contact`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="open-score-card__action open-score-card__action--contact"
                        style={pillStyle}
                        onClick={(event) => {
                            handleActionClick(event);
                            trackAction('contact');
                        }}
                    >
                        Contact
                    </a>
                </div>
            </div>

            {showEventPopup && (
                <EventPopup
                    events={singleDayEvents}
                    careHomeName={careHome.organisation_name}
                    regulatorId={careHome.reg_id}
                    userId={userId}
                    startingPostCode={startingPostCode}
                    searchPosition={searchPosition}
                    onClose={() => setShowEventPopup(false)}
                />
            )}
        </article>
    );
};

export default OpenScoreCard;
